import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import employeeCompetencyService, {
    type AddEmployeeCompetencyPayload,
    type EffectiveCompetency,
    type EmployeeCompetencyOverlayPayload,
    type RemoveEmployeeCompetencyPayload,
} from './employeeCompetencyService';

import type { QueryClient } from '@tanstack/react-query';

import {
    CompetencyQueryKey,
    EmployeeCompetencyQueryKey,
    ProfileAssignmentQueryKey,
} from '@/constants/QueryKeys';

const invalidateEmployeeCompetencies = (queryClient: QueryClient) => {
    // Assigning/removing a competency can change how many employees effectively have it,
    // which feeds the competency cards. Refresh the competency groups so any derived
    // counts on the cards (e.g. assignment indicators) stay in sync.
    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
    });
    // The individually-added employee list and the competency impact read model both
    // reflect per-employee competency changes, so refresh them too.
    queryClient.invalidateQueries({
        queryKey: [EmployeeCompetencyQueryKey.INDIVIDUAL_EMPLOYEES],
    });
    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_IMPACT],
    });
};

const useAddEmployeeCompetency = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: AddEmployeeCompetencyPayload) =>
            employeeCompetencyService.addEmployeeCompetency(payload),
        onSuccess: () => invalidateEmployeeCompetencies(queryClient),
    });
};

const useRemoveEmployeeCompetency = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: RemoveEmployeeCompetencyPayload) =>
            employeeCompetencyService.removeEmployeeCompetency(payload),
        onSuccess: () => invalidateEmployeeCompetencies(queryClient),
    });
};

// Persists one employee's full competency overlay (added/removed sets). Refreshes the
// effective competencies, the competency cards' derived counts, and the roster (whose
// per-employee customization is what changed).
const useSetEmployeeCompetencyOverlay = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: EmployeeCompetencyOverlayPayload) =>
            employeeCompetencyService.setEmployeeCompetencyOverlay(payload),
        onSuccess: () => {
            invalidateEmployeeCompetencies(queryClient);
            queryClient.invalidateQueries({
                queryKey: [ProfileAssignmentQueryKey.ROSTER],
            });
        },
    });
};

/**
 * Returns a resolver that, for a given competency, tells which of a set of employees already
 * effectively have it. Designed to be dropped straight into the assign-to-employees wizard's
 * `resolveAlreadyAssignedIds` prop:
 *
 *     const resolveAlready = useEmployeesWithCompetency(competency.id);
 *     <AssignToEmployeesWizard resolveAlreadyAssignedIds={resolveAlready} ... />
 */
const useEmployeesWithCompetency = (competencyId: string) => {
    return useCallback(
        (employeeIds: string[]) =>
            employeeCompetencyService.getEmployeesWithCompetency({ competencyId, employeeIds }),
        [competencyId],
    );
};

/**
 * Returns the ids of employees who have the competency added individually (outside any profile).
 * Backs the "Profils assignés" modal's "Ajoutée individuellement" drill-in.
 */
const useGetIndividualEmployees = (competencyId: string, enabled = true) => {
    return useQuery<string[]>({
        queryKey: [EmployeeCompetencyQueryKey.INDIVIDUAL_EMPLOYEES, competencyId],
        queryFn: () => employeeCompetencyService.getIndividualEmployees(competencyId),
        enabled,
    });
};

/**
 * Returns the employee's effective competencies: profile competencies minus overlay
 * removals, plus overlay additions. Used by the Manager Assessment to ensure all
 * assigned skills (including individually-added ones) are included (Bug 14081).
 */
const useGetEffectiveCompetencies = (employeeId?: string) => {
    return useQuery<EffectiveCompetency[]>({
        queryKey: [EmployeeCompetencyQueryKey.EFFECTIVE, employeeId],
        queryFn: () => employeeCompetencyService.getEffectiveCompetencies(employeeId!),
        enabled: !!employeeId,
    });
};

export {
    useAddEmployeeCompetency,
    useRemoveEmployeeCompetency,
    useEmployeesWithCompetency,
    useGetEffectiveCompetencies,
    useGetIndividualEmployees,
    useSetEmployeeCompetencyOverlay,
};

export type { EffectiveCompetency };
